import type { ReactElement } from "react";

import { ChevronDown, ChevronRight } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { FuzzyMatchRange } from "app/pages/sales-intelligence/services/fuzzy-search/types";
import { segmentDisplayForSearchHighlight } from "app/pages/sales-intelligence/services/fuzzy-search/highlight";
import { segmentStringBySearchTerm } from "app/pages/sales-intelligence/utils/strings";
import type { TaxonomyTreeRow } from "app/pages/sales-intelligence/utils/taxonomy-tree/types";

import { StyledHighlightFragment } from "../StyledHighlightFragment";

import { StyledChevronButton, StyledChevronSpacer, StyledCount, StyledTruncatedLabel } from "./styles";
import type { TreeSelectionMode } from "./types";

const INDENT_PER_LEVEL_PX = 8;

export type TreeRowOptionProps<TId extends string> = {
    /**
     * The row's collection key. react-aria reads it off *this* element's props to key the virtualized
     * collection (and clones it back in once resolved), so it must be declared here and passed through —
     * an `id` only on the inner `Combobox.Option` is invisible to the collection builder.
     */
    id: TId;
    row: TaxonomyTreeRow<TId>;
    selection: TreeSelectionMode;
    dataAutomation: string;
    expandLabel: string;
    collapseLabel: string;
    onToggleChecked: (id: TId) => void;
    onToggleExpanded: (id: TId) => void;
    highlightRanges?: readonly FuzzyMatchRange[];
    searchTerm: string;
    locale: string;
};

export const TreeRowOption = <TId extends string>({
    id,
    row,
    selection,
    dataAutomation,
    expandLabel,
    collapseLabel,
    onToggleChecked,
    onToggleExpanded,
    highlightRanges,
    searchTerm,
    locale,
}: TreeRowOptionProps<TId>): ReactElement => {
    const { node, depth, checkState, hasChildren, isExpanded, count } = row;
    const isChecked = checkState === "checked";
    const labelFragments = segmentDisplayForSearchHighlight(
        node.name,
        searchTerm,
        highlightRanges,
        locale,
        segmentStringBySearchTerm,
    );

    return (
        <Combobox.Option
            id={id}
            textValue={node.name}
            // Single-select carries its selection on the row itself, the way every other single-select
            // combobox here does; a checkbox would promise a second pick the filter cannot hold.
            isSelected={selection === "single" && isChecked}
            onClick={() => onToggleChecked(node.id)}
            data-automation={`${dataAutomation}-row-${node.id}`}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                width="100%"
                padding={`0 0 0 ${depth * INDENT_PER_LEVEL_PX}px`}
            >
                <Flex
                    alignItems="center"
                    gap="4px"
                    flex="1"
                    minWidth={0}
                >
                    {hasChildren ? (
                        <StyledChevronButton
                            as="button"
                            type="button"
                            alignItems="center"
                            justifyContent="center"
                            // The chevron lives inside the option row, so a bare click would also toggle
                            // the checkbox. Expanding a node must never change the selection.
                            onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                onToggleExpanded(node.id);
                            }}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? collapseLabel : expandLabel}
                            data-automation={`${dataAutomation}-chevron-${node.id}`}
                        >
                            {isExpanded ? (
                                <ChevronDown
                                    size="xs"
                                    color={colorsPalettes.carbon[300]}
                                />
                            ) : (
                                <ChevronRight
                                    size="xs"
                                    color={colorsPalettes.carbon[300]}
                                />
                            )}
                        </StyledChevronButton>
                    ) : (
                        <StyledChevronSpacer />
                    )}

                    <StyledTruncatedLabel
                        alignItems="center"
                        flex="1"
                        minWidth={0}
                    >
                        <Combobox.OptionLabel>
                            {labelFragments.map((fragment, index) => (
                                <StyledHighlightFragment
                                    key={`${index}-${fragment.text}`}
                                    $highlighted={fragment.highlighted}
                                >
                                    {fragment.text}
                                </StyledHighlightFragment>
                            ))}
                        </Combobox.OptionLabel>
                    </StyledTruncatedLabel>

                    {count === undefined ? null : (
                        <StyledCount>
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                {count}
                            </Text>
                        </StyledCount>
                    )}
                </Flex>

                {selection === "multiple" ? (
                    <Flex
                        alignItems="center"
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggleChecked(node.id);
                        }}
                        data-automation={`${dataAutomation}-checkbox-${node.id}`}
                    >
                        <Combobox.OptionCheckbox
                            isChecked={isChecked}
                            halfSelected={checkState === "indeterminate"}
                        />
                    </Flex>
                ) : null}
            </Flex>
        </Combobox.Option>
    );
};
